import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

// Reduced from 15 languages to en + id on 2026-09-29 (see REMOVED_LOCALES in
// scripts/redirects.mjs). scripts/check-locales.mjs fails the build if this list,
// the locale files on disk and the other locale lists ever disagree.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',            flag: '🇬🇧', nativeName: 'English'           },
  { code: 'id', label: 'Indonesian',         flag: '🇮🇩', nativeName: 'Bahasa Indonesia'   },
];

// Each locale ships as its own lazily-loaded chunk (see ./locales/*.ts). Only the
// active language is fetched at runtime instead of bundling all 9 into the main JS.
// Switching languages loads the target locale's chunk on demand.
export const i18nReady = i18n
  .use(
    resourcesToBackend(
      (lng: string) => import(`./locales/${lng}.ts`).then((m) => m.default),
    ),
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'id'],
    // Normalize 'en-US' → 'en' so the backend never tries to fetch a regional chunk.
    load: 'languageOnly',
    detection: {
      // Detection order: browser language only — path-based routing handles locale selection.
      // We do NOT read ?lng= query strings (removed: caused Google redirect-loop reports) and
      // we do NOT cache browser language to localStorage — IP detection handles persistence.
      order: ['navigator'],
      caches: [],
    },
    interpolation: {
      escapeValue: false,
    },
    // Never suspend on missing resources; render fallback text until the chunk loads.
    react: {
      useSuspense: false,
    },
  });

// ─── Country → Language mapping ───────────────────────────────────────────────
const COUNTRY_LANG: Record<string, string> = {
  // Indonesian
  ID: 'id',
};

/**
 * IP geolocation ile ülkeyi tespit et ve dili ayarla.
 * Sadece kullanıcı daha önce dil seçmemişse devreye girer.
 */
export async function detectLanguageFromIP(): Promise<void> {
  // Kullanıcı manuel dil seçtiyse dokunma
  // A returning visitor may have a since-removed locale (ru, es, ...) stored from
  // before 2026-09-29; ignore it rather than asking i18next for a language that
  // no longer ships.
  const isSupported = (code: string) => SUPPORTED_LANGUAGES.some((l) => l.code === code);
  const userPicked = localStorage.getItem('cryptperks_lang_user');
  if (userPicked && isSupported(userPicked)) {
    i18n.changeLanguage(userPicked);
    return;
  }

  try {
    // Server-side tespiti — CORS sorunu yok, gerçek IP'yi okur
    const res = await fetch('/api/geoip', { signal: AbortSignal.timeout(4000) });
    const data = await res.json() as { lang: string; country: string | null };
    if (data.lang && data.lang !== 'en' && isSupported(data.lang)) {
      i18n.changeLanguage(data.lang);
    }
  } catch {
    // Sessizce geç — varsayılan dil zaten ayarlandı
  }
}

export default i18n;
